import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, Router } from '@angular/router';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, take, timeout } from 'rxjs';

import { AppSelectors } from '../app/app.selectors';
import { CompanySelectors } from '../app-entities/company-users/company.selectors';
import { APP_ROUTES } from '../app-global/router/app-routes';
import { SessionSelectors } from '../app-global/session/session.selectors';

import { unauthenticatedTarget } from './auth.guard';
import { GUARD_TIMEOUT_MS } from './guard-timeout';

/**
 * `appReady` never fires during a deferred restore, so this timeout path is exactly the path an
 * offline launch takes, and it must send the same place `UserIsAuthenticatedGuardFn` does rather
 * than racing it to `/login`.
 */
export const AllowAccessGuardFn: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const store = inject(Store);
  const router = inject(Router);
  return store.select(AppSelectors.appReady).pipe(
    filter(appReady => !!appReady),
    concatLatestFrom(() => store.select(CompanySelectors.selectCompanyAccessIsActive)),
    map(([, companyIsActive]) => {
      if (companyIsActive) {
        return true;
      }

      return router.createUrlTree(['/', APP_ROUTES.NoAccess]);
    }),
    timeout(GUARD_TIMEOUT_MS),
    catchError(() =>
      store.select(SessionSelectors.isRestoreDeferred).pipe(
        take(1),
        map(isRestoreDeferred => unauthenticatedTarget(router, isRestoreDeferred))
      )
    )
  );
};
